import { memo } from "react";

/* ── Twinkling dots ─────────────────────────────────────────── */
const twinkleDots = Array.from({ length: 45 }, (_, i) => ({
  id: i,
  left: `${Math.random() * 100}%`,
  top: `${Math.random() * 100}%`,
  size: 1.5 + Math.random() * 2.5,
  delay: Math.random() * 6,
  duration: 3 + Math.random() * 4,
  color:
    i % 3 === 0
      ? "rgba(124, 77, 255, 0.6)"
      : i % 3 === 1
      ? "rgba(39, 184, 255, 0.55)"
      : "rgba(255, 255, 255, 0.35)",
}));

/* ── Circuit path data ──────────────────────────────────────── */
const circuitPaths = [
  "M0 200 L120 200 L120 80 L300 80",
  "M400 400 L400 280 L550 280 L550 150",
  "M100 500 L250 500 L250 350 L400 350",
  "M600 100 L600 250 L750 250 L750 400",
  "M800 300 L700 300 L700 450 L580 450",
  "M200 50 L200 180 L350 180",
];

const AnimatedBackground = () => {
  return (
    <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
      {/* Base gradient */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 20% 20%, rgba(124, 77, 255, 0.08), transparent 50%), " +
            "radial-gradient(ellipse at 80% 80%, rgba(39, 184, 255, 0.06), transparent 50%), " +
            "radial-gradient(ellipse at 50% 50%, rgba(124, 77, 255, 0.04), transparent 60%)",
        }}
      />

      {/* Animated grid */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(124, 77, 255, 0.04) 1px, transparent 1px), " +
            "linear-gradient(90deg, rgba(124, 77, 255, 0.04) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          animation: "grid-drift 20s linear infinite",
        }}
      />

      {/* Moving glow blobs */}
      <div
        className="absolute rounded-full blur-[150px]"
        style={{
          width: 500,
          height: 500,
          background: "radial-gradient(circle, rgba(124, 77, 255, 0.15), transparent 70%)",
          animation: "blob-move-1 25s ease-in-out infinite",
          willChange: "transform",
        }}
      />
      <div
        className="absolute rounded-full blur-[130px]"
        style={{
          width: 450,
          height: 450,
          background: "radial-gradient(circle, rgba(39, 184, 255, 0.12), transparent 70%)",
          animation: "blob-move-2 30s ease-in-out infinite",
          willChange: "transform",
        }}
      />
      <div
        className="absolute rounded-full blur-[120px]"
        style={{
          width: 380,
          height: 380,
          background: "radial-gradient(circle, rgba(124, 77, 255, 0.1), transparent 70%)",
          animation: "blob-move-3 22s ease-in-out infinite",
          willChange: "transform",
        }}
      />

      {/* Twinkling dots */}
      {twinkleDots.map((dot) => (
        <div
          key={dot.id}
          className="absolute rounded-full"
          style={{
            left: dot.left,
            top: dot.top,
            width: dot.size,
            height: dot.size,
            background: dot.color,
            boxShadow: `0 0 ${dot.size * 3}px ${dot.color}`,
            animation: `twinkle ${dot.duration}s ease-in-out ${dot.delay}s infinite`,
            willChange: "opacity",
          }}
        />
      ))}

      {/* Circuit lines */}
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 900 600"
        preserveAspectRatio="none"
        style={{ opacity: 0.15 }}
      >
        {circuitPaths.map((d, i) => (
          <path
            key={i}
            d={d}
            fill="none"
            stroke={i % 2 === 0 ? "#7C4DFF" : "#27B8FF"}
            strokeWidth="1"
            strokeDasharray="8 12"
            style={{
              animation: `circuit-flow ${6 + i * 1.5}s linear ${i * 0.8}s infinite`,
            }}
          />
        ))}
        {/* Circuit junction dots */}
        {[
          [120, 200],
          [120, 80],
          [400, 280],
          [550, 280],
          [250, 500],
          [250, 350],
          [600, 250],
          [750, 250],
          [700, 300],
          [700, 450],
          [200, 180],
        ].map(([cx, cy], i) => (
          <circle
            key={`junction-${i}`}
            cx={cx}
            cy={cy}
            r="2.5"
            fill={i % 2 === 0 ? "#7C4DFF" : "#27B8FF"}
            style={{
              animation: `twinkle ${3 + (i % 3)}s ease-in-out ${i * 0.5}s infinite`,
            }}
          />
        ))}
      </svg>

      {/* Scan line */}
      <div
        className="absolute left-0 right-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(124, 77, 255, 0.3), rgba(39, 184, 255, 0.2), transparent)",
          animation: "scan-line-move 8s linear infinite",
          willChange: "transform",
        }}
      />
    </div>
  );
};

export default memo(AnimatedBackground);
